import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Search, 
  Sparkles, 
  Home, 
  DollarSign, 
  Calendar,
  Bed,
  Bath,
  Square,
  Loader2,
  Filter,
  X,
  Calculator,
  ChevronDown,
  ChevronUp,
  MapPin,
  Building2,
  TreePine,
  Landmark,
  ArrowRight,
  CheckCircle2,
  Clock,
  FileCheck,
  Info,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const PROPERTY_TYPES = [
  { value: 'single_family', label: 'Single Family', icon: Home },
  { value: 'condo', label: 'Condo', icon: Building2 },
  { value: 'townhouse', label: 'Townhouse', icon: Building2 },
  { value: 'multi_family', label: 'Multi Family', icon: Landmark },
  { value: 'land', label: 'Land', icon: TreePine },
];

const LISTING_STATUS = [
  { value: 'all', label: 'All Listings', color: 'bg-slate-500' },
  { value: 'active', label: 'Active', color: 'bg-green-500' },
  { value: 'pending', label: 'Pending', color: 'bg-amber-500' },
  { value: 'closed', label: 'Closed/Sold', color: 'bg-blue-500' },
];

const DATE_RANGES = [
  { value: '30', label: 'Last 30 Days' },
  { value: '60', label: 'Last 60 Days' },
  { value: '90', label: 'Last 90 Days' },
  { value: '180', label: 'Last 6 Months' },
  { value: '365', label: 'Last Year' },
];

const CompsSearchPage = () => {
  const navigate = useNavigate();
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedComps, setSelectedComps] = useState([]);
  
  // Search form
  const [searchForm, setSearchForm] = useState({
    address: '',
    city: '',
    state: '',
    zipcode: ''
  });

  // Filters
  const [filters, setFilters] = useState({
    dateRange: '90',
    status: 'all',
    propertyType: 'single_family',
    bedsMin: '',
    bedsMax: '',
    bathsMin: '',
    bathsMax: '',
    sqftMin: '',
    sqftMax: '',
    lotSizeMin: '',
    lotSizeMax: '',
    priceMin: '',
    priceMax: ''
  });

  const handleSearch = async () => {
    if (!searchForm.city || !searchForm.state) {
      toast.error('Please enter at least city and state');
      return;
    }

    setSearching(true);
    setResults([]);
    setSelectedComps([]);

    try {
      const response = await aiAPI.searchComps({
        address: searchForm.address,
        city: searchForm.city,
        state: searchForm.state,
        zipcode: searchForm.zipcode,
        num_comps: 10,
        filters: filters
      });
      
      const comps = response.data.suggested_comps || [];
      setResults(comps);
      
      if (comps.length > 0) {
        toast.success(`Found ${comps.length} properties`);
      } else {
        toast.info('No properties found. Try adjusting your filters.');
      }
    } catch (error) {
      toast.error('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const toggleCompSelection = (comp) => {
    setSelectedComps(prev => {
      const isSelected = prev.some(c => c.address === comp.address);
      if (isSelected) {
        return prev.filter(c => c.address !== comp.address);
      } else {
        return [...prev, comp];
      }
    });
  };

  const handleAnalyzeSelected = () => {
    if (selectedComps.length === 0) {
      toast.error('Select at least one property to analyze');
      return;
    }

    // Calculate average values from selected comps
    const avgPrice = Math.round(selectedComps.reduce((sum, c) => sum + (c.sale_price || 0), 0) / selectedComps.length);
    const avgSqft = Math.round(selectedComps.reduce((sum, c) => sum + (c.sqft || 0), 0) / selectedComps.length);
    const avgBeds = Math.round(selectedComps.reduce((sum, c) => sum + (c.beds || 0), 0) / selectedComps.length);
    const avgBaths = Math.round(selectedComps.reduce((sum, c) => sum + (c.baths || 0), 0) / selectedComps.length * 10) / 10;

    // Navigate to underwriting with pre-filled data
    navigate('/underwriting', {
      state: {
        prefillData: {
          address: searchForm.address || `${searchForm.city}, ${searchForm.state}`,
          city: searchForm.city,
          state: searchForm.state,
          zipcode: searchForm.zipcode,
          arv_estimate: avgPrice,
          sqft: avgSqft,
          beds: avgBeds,
          baths: avgBaths,
          comps: selectedComps,
          property_type: filters.propertyType
        }
      }
    });
  };

  const handleAnalyzeSingle = (comp) => {
    navigate('/underwriting', {
      state: {
        prefillData: {
          address: comp.address,
          city: searchForm.city,
          state: searchForm.state,
          zipcode: searchForm.zipcode,
          arv_estimate: comp.sale_price,
          sqft: comp.sqft,
          beds: comp.beds,
          baths: comp.baths,
          property_type: comp.property_type || filters.propertyType
        }
      }
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'active': { label: 'Active', class: 'bg-green-500/20 text-green-400 border-green-500/30' },
      'pending': { label: 'Pending', class: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
      'closed': { label: 'Sold', class: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    };
    const config = statusConfig[status] || statusConfig['closed'];
    return <Badge variant="outline" className={`${config.class} text-xs`}>{config.label}</Badge>;
  };

  const clearFilters = () => {
    setFilters({
      dateRange: '90',
      status: 'all',
      propertyType: 'single_family',
      bedsMin: '',
      bedsMax: '',
      bathsMin: '',
      bathsMax: '',
      sqftMin: '',
      sqftMax: '',
      lotSizeMin: '',
      lotSizeMax: '',
      priceMin: '',
      priceMax: ''
    });
  };

  return (
    <div className="space-y-6" data-testid="comps-search-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold font-heading tracking-tight">Find Comps</h1>
          <p className="text-muted-foreground mt-1">
            Search comparable properties and analyze deals with AI
          </p>
        </div>
        {selectedComps.length > 0 && (
          <Button 
            onClick={handleAnalyzeSelected}
            className="bg-primary text-primary-foreground rounded-sm btn-scale shadow-lg shadow-primary/20"
          >
            <Calculator className="w-4 h-4 mr-2" />
            Analyze {selectedComps.length} Selected
          </Button>
        )}
      </div>

      {/* AI Disclaimer */}
      <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm">
        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-400">AI-Generated Estimates</p>
          <p className="text-xs text-muted-foreground mt-1">
            These are AI-generated property estimates for deal analysis purposes only. 
            Always verify actual listings with MLS data, county records, or a licensed real estate agent before making offers.
          </p>
        </div>
      </div>

      {/* Search Section */}
      <Card className="border-border bg-gradient-to-br from-primary/10 via-accent/5 to-transparent rounded-sm">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs text-muted-foreground">Street Address (Optional)</Label>
              <Input
                placeholder="123 Main Street"
                value={searchForm.address}
                onChange={(e) => setSearchForm(prev => ({ ...prev, address: e.target.value }))}
                className="bg-background"
                data-testid="comp-address-input"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">City *</Label>
              <Input
                placeholder="Austin"
                value={searchForm.city}
                onChange={(e) => setSearchForm(prev => ({ ...prev, city: e.target.value }))}
                className="bg-background"
                data-testid="comp-city-input"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">State *</Label>
              <Select 
                value={searchForm.state} 
                onValueChange={(v) => setSearchForm(prev => ({ ...prev, state: v }))}
              >
                <SelectTrigger className="bg-background" data-testid="comp-state-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">ZIP Code</Label>
              <Input
                placeholder="78701"
                value={searchForm.zipcode}
                onChange={(e) => setSearchForm(prev => ({ ...prev, zipcode: e.target.value }))}
                className="bg-background"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-3 mt-4">
            <Button 
              onClick={handleSearch}
              disabled={searching}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm flex-1 md:flex-none"
              data-testid="search-comps-btn"
            >
              {searching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Search with AI
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className="border-border"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="border-border bg-card rounded-sm">
          <CardHeader className="border-b border-border py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Search Filters</CardTitle>
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-muted-foreground">
                <X className="w-3 h-3 mr-1" />
                Clear All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {/* Date Range */}
              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Date Range
                </Label>
                <Select value={filters.dateRange} onValueChange={(v) => setFilters(prev => ({ ...prev, dateRange: v }))}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_RANGES.map(range => (
                      <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status */}
              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <FileCheck className="w-3 h-3" />
                  Status
                </Label>
                <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LISTING_STATUS.map(status => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Property Type */}
              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Home className="w-3 h-3" />
                  Property Type
                </Label>
                <Select value={filters.propertyType} onValueChange={(v) => setFilters(prev => ({ ...prev, propertyType: v }))}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Bedrooms */}
              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Bed className="w-3 h-3" />
                  Bedrooms
                </Label>
                <div className="flex gap-1">
                  <Input 
                    type="number" 
                    placeholder="Min" 
                    value={filters.bedsMin}
                    onChange={(e) => setFilters(prev => ({ ...prev, bedsMin: e.target.value }))}
                    className="h-9 text-sm"
                  />
                  <Input 
                    type="number" 
                    placeholder="Max" 
                    value={filters.bedsMax}
                    onChange={(e) => setFilters(prev => ({ ...prev, bedsMax: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              {/* Bathrooms */}
              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Bath className="w-3 h-3" />
                  Bathrooms
                </Label>
                <div className="flex gap-1">
                  <Input 
                    type="number" 
                    step="0.5"
                    placeholder="Min" 
                    value={filters.bathsMin}
                    onChange={(e) => setFilters(prev => ({ ...prev, bathsMin: e.target.value }))}
                    className="h-9 text-sm"
                  />
                  <Input 
                    type="number" 
                    step="0.5"
                    placeholder="Max" 
                    value={filters.bathsMax}
                    onChange={(e) => setFilters(prev => ({ ...prev, bathsMax: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              {/* Sqft */}
              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Square className="w-3 h-3" />
                  Sqft
                </Label>
                <div className="flex gap-1">
                  <Input 
                    type="number" 
                    placeholder="Min" 
                    value={filters.sqftMin}
                    onChange={(e) => setFilters(prev => ({ ...prev, sqftMin: e.target.value }))}
                    className="h-9 text-sm"
                  />
                  <Input 
                    type="number" 
                    placeholder="Max" 
                    value={filters.sqftMax}
                    onChange={(e) => setFilters(prev => ({ ...prev, sqftMax: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              {/* Lot Size */}
              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <TreePine className="w-3 h-3" />
                  Lot Size (sqft)
                </Label>
                <div className="flex gap-1">
                  <Input 
                    type="number" 
                    placeholder="Min" 
                    value={filters.lotSizeMin}
                    onChange={(e) => setFilters(prev => ({ ...prev, lotSizeMin: e.target.value }))}
                    className="h-9 text-sm"
                  />
                  <Input 
                    type="number" 
                    placeholder="Max" 
                    value={filters.lotSizeMax}
                    onChange={(e) => setFilters(prev => ({ ...prev, lotSizeMax: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              {/* Price Range */}
              <div className="md:col-span-2">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  Price Range
                </Label>
                <div className="flex gap-1">
                  <Input 
                    type="number" 
                    placeholder="Min Price" 
                    value={filters.priceMin}
                    onChange={(e) => setFilters(prev => ({ ...prev, priceMin: e.target.value }))}
                    className="h-9 text-sm"
                  />
                  <Input 
                    type="number" 
                    placeholder="Max Price" 
                    value={filters.priceMax}
                    onChange={(e) => setFilters(prev => ({ ...prev, priceMax: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results.length > 0 && (
        <Card className="border-border bg-card rounded-sm overflow-hidden">
          <CardHeader className="border-b border-border bg-gradient-to-r from-secondary/50 to-transparent">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <CardTitle className="font-heading">Search Results</CardTitle>
                <Badge className="bg-primary/20 text-primary">{results.length} properties</Badge>
              </div>
              {selectedComps.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  {selectedComps.length} selected
                </p>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {results.map((comp, index) => {
                const isSelected = selectedComps.some(c => c.address === comp.address);
                return (
                  <div 
                    key={index} 
                    className={`p-4 transition-all cursor-pointer ${isSelected ? 'bg-primary/10 border-l-4 border-l-primary' : 'hover:bg-secondary/30'}`}
                    onClick={() => toggleCompSelection(comp)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        {/* Selection indicator */}
                        <div className={`w-6 h-6 rounded-sm border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all ${isSelected ? 'bg-primary border-primary' : 'border-border'}`}>
                          {isSelected && <CheckCircle2 className="w-4 h-4 text-primary-foreground" />}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h4 className="font-semibold">{comp.address}</h4>
                            {getStatusBadge(comp.status || 'closed')}
                            <Badge variant="outline" className="text-xs">
                              {PROPERTY_TYPES.find(t => t.value === comp.property_type)?.label || 'Single Family'}
                            </Badge>
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-2">
                            <span className="flex items-center gap-1 font-mono font-semibold text-green-400">
                              <DollarSign className="w-3 h-3" />
                              {formatCurrency(comp.sale_price)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {comp.sale_date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Bed className="w-3 h-3" />
                              {comp.beds} beds
                            </span>
                            <span className="flex items-center gap-1">
                              <Bath className="w-3 h-3" />
                              {comp.baths} baths
                            </span>
                            <span className="flex items-center gap-1">
                              <Square className="w-3 h-3" />
                              {comp.sqft?.toLocaleString()} sqft
                            </span>
                            {comp.lot_size && (
                              <span className="flex items-center gap-1">
                                <TreePine className="w-3 h-3" />
                                {comp.lot_size?.toLocaleString()} lot
                              </span>
                            )}
                            {comp.price_per_sqft && (
                              <span className="flex items-center gap-1 text-amber-400">
                                ${comp.price_per_sqft}/sqft
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAnalyzeSingle(comp);
                        }}
                        className="border-primary text-primary hover:bg-primary/10 flex-shrink-0"
                      >
                        <Calculator className="w-4 h-4 mr-1" />
                        Analyze
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {results.length === 0 && !searching && (
        <Card className="border-border bg-card rounded-sm">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mb-4">
              <Search className="w-10 h-10 text-primary/50" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Search for Comparable Properties</h3>
            <p className="text-sm text-muted-foreground mb-2 max-w-md mx-auto">
              Enter a location above and use AI to find comparable properties in the area. 
              Select comps to analyze potential deals.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              <Badge variant="outline" className="text-xs">Single Family</Badge>
              <Badge variant="outline" className="text-xs">Condos</Badge>
              <Badge variant="outline" className="text-xs">Townhouses</Badge>
              <Badge variant="outline" className="text-xs">Multi Family</Badge>
              <Badge variant="outline" className="text-xs">Land</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selected Comps Summary Bar */}
      {selectedComps.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <Card className="border-primary bg-card shadow-2xl shadow-primary/20 rounded-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Badge className="bg-primary text-primary-foreground">{selectedComps.length}</Badge>
                <span className="text-sm font-medium">properties selected</span>
              </div>
              <div className="text-sm text-muted-foreground">
                Avg: {formatCurrency(selectedComps.reduce((sum, c) => sum + (c.sale_price || 0), 0) / selectedComps.length)}
              </div>
              <Button 
                onClick={handleAnalyzeSelected}
                className="bg-primary text-primary-foreground rounded-sm"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Analyze Selected
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSelectedComps([])}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CompsSearchPage;
